import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { TableProps } from 'antd';
import { SorterResult } from 'antd/lib/table/interface';
import { Divider, Fx, Spin, Stack } from 'Components';
import { createSortDescription } from 'Components/Table/createColumns';
import { useStores } from 'Hooks/useStore';
import { getIsPending } from 'Stores/util';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';
import {
	getAbsDiffValue,
	getTrendDirection
} from 'Pages/Dashboard/Dashboard.helpers';
import {
	OverviewGroupSummary,
	OverviewScores,
	WidgetData
} from 'Services/Api/Dashboard/Types';
import {
	getProvidersForTenant,
	getComplianceSummary,
	getVenzaProperties
} from 'Services/Api/PciCompliance/SAQDashboard.Service';
import StatsCard from './components/StatsCard/StatsCard';
import GroupSummaryTable from './GroupSummaryTable/GroupSummaryTable';
import { getLeadPath } from 'Pages/App/AppLayout/AppHeader/AppHeader.helpers';
import styles from './NewDashboard.module.scss';

type ScoreKey = keyof Pick<
	OverviewScores,
	'phishingScore' | 'elearningScore' | 'saqCompliance' | 'scanning'
>;

const STATS_CARD_CONFIG: Array<{
	key: ScoreKey;
	title: string;
	tooltip: string;
}> = [
	{
		key: 'phishingScore',
		title: 'newDashboard.statsCard.phishing.title',
		tooltip: 'newDashboard.statsCard.phishing.tooltip'
	},
	{
		key: 'elearningScore',
		title: 'newDashboard.statsCard.training.title',
		tooltip: 'newDashboard.statsCard.training.tooltip'
	},
	{
		key: 'saqCompliance',
		title: 'newDashboard.statsCard.saqCompliance.title',
		tooltip: 'newDashboard.statsCard.saqCompliance.tooltip'
	},
	{
		key: 'scanning',
		title: 'newDashboard.statsCard.scanning.title',
		tooltip: 'newDashboard.statsCard.scanning.tooltip'
	}
];

interface PropertyItem {
    property_id: number;
    property_name: string;
    status: 'notstarted' | 'pass' | 'fail' | 'incomplete';
    due_date: string | null; 
    last_change_date: string | null;
}

function NewDashboard() {
	const { dashboardStore, authStore, systemsStore } = useStores();
	const isSuperAdmin = authStore.isCurrentUserSuperAdmin;

	const { filters } = useDashboardContext();
	const [saqScore, setSaqScore] = useState<number | null>(null);
	const [scanningScore, setScanningScore] = useState<number | null>(null);
	const [saqTableData, setSaqTableData] = useState<PropertyItem[]>([]);
	const [scanningTableData, setScanningTableData] = useState<PropertyItem[]>([]);

	const { overview, status } = dashboardStore;
	const { pathname } = useLocation();

	const awstokenbracket = authStore;
	const listallawstokenbracket = JSON.stringify(awstokenbracket);
	const newtokenlist = JSON.parse(listallawstokenbracket);

	let isuperadm = '';
	const checkrole = JSON.stringify(newtokenlist.currentUser.primarySystemRole.id);
	if (checkrole == '1') isuperadm = '&awsUserIsSuperAdmin=true';

	const leadPath = getLeadPath(pathname);
	localStorage.setItem('APIadmin', isuperadm);

	if (typeof leadPath === 'string') localStorage.setItem('APIpath', leadPath);
    else localStorage.setItem('APIpath', '');

	const adminGroupId = !isSuperAdmin ? systemsStore.selectedTenant.value : undefined;
	const tenantId = filters?.id ?? adminGroupId;

	const fetchAllPaginatedProperties = async (
		tenantId: number,
		providerId: number,
		perPage = 50
	): Promise<PropertyItem[]> => {
		try {
			const firstPage = await getVenzaProperties({
				tenant_id: tenantId,
				provider_id: providerId,
				per_page: perPage,
				page: 1
			});
	
			const firstPageData = firstPage?.data || [];
			const total = firstPage?.meta?.total || firstPageData.length;
	
			const totalPages = Math.ceil(total / perPage);
	
			if (totalPages <= 1) return firstPageData;
	
			const requests = Array.from(
				{ length: totalPages - 1 },
				(_, index) =>
					getVenzaProperties({
						tenant_id: tenantId,
						provider_id: providerId,
						per_page: perPage,
						page: index + 2
					})
			);
	
			const responses = await Promise.all(requests);
	
			return [
				...firstPageData,
				...responses.flatMap(response => response?.data || [])
			];
		} catch (error) {
			console.error(`Failed to fetch for provider ${providerId}:`, error);
			return [];
		}
	};

	useEffect(() => {
		if(!(!(filters?.id || 0) && filters?.isProperty)){
			dashboardStore.getOverviewScores(filters);
			dashboardStore.getOverviewGroupSummary(filters);
		}
		// eslint-disable-next-line
	}, [filters]);

	useEffect(() => {
		if (!tenantId) {
			setSaqScore(null);
			setScanningScore(null);
			return;
		}

		let providers : { provider_id: number; provider_name: string, status: string }[] = [];
		const fetchScore = async (envKey: string, setter: (v: number | null) => void, tableDataSetter: (v: PropertyItem[]) => void) => {
			try {
				if (!providers.length) {
					const result = await getProvidersForTenant(String(tenantId));
					providers = Array.isArray(result.data) ? result.data : [];
				}

				const envName = (process.env[envKey] || '').trim().toLowerCase();
				const matched = providers.find(x => x.provider_name.trim().toLowerCase() === envName);

				if (!matched) {
					setter(null);
					tableDataSetter([]);
					return;
				}
				const summary = await getComplianceSummary(tenantId, matched.provider_id);
				const response = await fetchAllPaginatedProperties(tenantId, matched.provider_id);
				let filteredData: PropertyItem[] = [];
				if (envName === "scanning") {
					filteredData = response.reduce((acc: PropertyItem[], p: any) => {
						const status = p.status && p.status.trim() !== "" ? p.status : "notstarted";
						if (status === "pass" || status === "fail" || status === "notstarted") acc.push({ ...p, status });
						return acc;
					}, []);
				} else {
					filteredData = response.filter((p: any) => (p.status && p.status) !== "na") || [];
				}

				tableDataSetter(filteredData);
				setter(summary.compliant_percentage !== undefined ? parseFloat(summary.compliant_percentage) : 0);
			} catch {
				setter(null);
				tableDataSetter([]);
			}
		};

		(async function(){
			await fetchScore('REACT_APP_SAQ_COMPLIANCE_DASHBOARD', setSaqScore, setSaqTableData);
			await fetchScore('REACT_APP_SCANNING_DASHBOARD', setScanningScore, setScanningTableData);
		})();
		// eslint-disable-next-line
	}, [tenantId]);

	const handleTableChange: TableProps<OverviewGroupSummary>['onChange'] = (
		_,
		__,
		sorter
	) => {
		const sort = createSortDescription<OverviewGroupSummary>(
			sorter as SorterResult<OverviewGroupSummary>
		);
		dashboardStore.getOverviewGroupSummary({
			...overview.groupSummary.params,
			sort
		});
	};

	const getCardData = (key: ScoreKey) => {
		if (key === 'saqCompliance') {
			return { value: saqScore ?? null, trend: null, trendDirection: null };
		} else if (key === 'scanning') {
			return { value: scanningScore ?? null, trend: null, trendDirection: null };
		}
		const data = overview.scores?.[key] as WidgetData | undefined | null;
		return {
			value: data?.current ?? null,
			trend: getAbsDiffValue(data?.current ?? null, data?.hystorical),
			trendDirection: getTrendDirection(
				data?.current ?? null,
				data?.hystorical
			)
		};
	};

	const tableData: OverviewGroupSummary[] = useMemo(() => {
		const overviewData = overview.groupSummary.data;
		if (!overviewData.length) return [];
		if (!saqTableData.length && !scanningTableData.length) return overviewData;

		const saqMap = new Map(saqTableData.map(item => [item.property_id, item]));
		const scanningMap = new Map(scanningTableData.map(item => [item.property_id, item]));
	
		return overviewData.map(item => ({
			...item,
			saqStatus: saqMap.get(item?.id || 0)?.status ?? "",
			scanningStatus: scanningMap.get(item?.id || 0)?.status ?? ""
		}));
	}, [overview.groupSummary.data, saqTableData, scanningTableData]);

	const scoresLoading = getIsPending(status.getOverviewScores);

	return (
		<Fx column grow={1}>
			<Stack>
				{scoresLoading ? (
					<div className={styles.spinWrapper}>
						<Spin />
					</div>
				) : (
					<Fx className={styles.statsRow} wrap>
						{STATS_CARD_CONFIG.map(({ key, title, tooltip }) => {
							const data = getCardData(key);
							return <Fx key={key} grow={1} className={styles.statCardWrapper}>
								<StatsCard title={title} tooltip={tooltip} {...data} />
							</Fx>
						})}
					</Fx>
				)}

				<GroupSummaryTable
					data={tableData}
					loading={getIsPending(status.getOverviewGroupSummary)}
					onChange={handleTableChange}
				/>

				<Divider className={styles.divider} />
			</Stack>
		</Fx>
	);
}

export default observer(NewDashboard);